import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import Api from "../util/API";
import { setSettingsSlice, setShipToCountry } from "./userSettingsSlice";
const productIdentity = (item) => {
    const idCandidate = [
        item?.meta?.id,
        item?.productId,
        item?.product_id,
        item?.productID,
        item?.id,
        item?._id,
        item?.product?.id,
        item?.product?._id,
        item?.product?.product_id,
        item?.product?.productId,
        item?.product?.item_id,
    ]
        .map((value) => String(value || "").trim())
        .find(Boolean);

    if (idCandidate) return `id:${idCandidate}`;

    const name = String(item?.meta?.name || item?.product?.product_title || item?.product?.title || "")
        .trim()
        .toLowerCase();
    const image = String(item?.meta?.image || item?.product?.image_url || item?.product?.image || "")
        .trim()
        .toLowerCase();

    if (!name && !image) return "";
    return `fallback:${name}|${image}`;
};
// Default to 'BD' if not set
const initialState = {
    products: [],
    status: "idle",
    selectedProduct: {},
    detailRequestId: null,
    error: null,
    lastSearchQuery: "",
    hasMore: true, // 🆕 ডেটা আর আছে কিনা
    currentPage: 1, // 🆕 এখন কোন পেজে আছো
    pagination: {
        page: 1,
        limit: 10,
        total: 0,
        hasMore: true,
    },
};

export const fetchProducts = createAsyncThunk(
    "products/fetchProducts",
    async (args = {}, thunkAPI) => {
        const state = typeof thunkAPI.getState === "function" ? thunkAPI.getState() : null;
        const settings = state?.userSettings || {};

        const country = String(args?.country ?? settings?.shipToCountry ?? "US")
            .trim()
            .toUpperCase();

        const currencyFallback = "USD";
        const currency = String(args?.currency ?? settings?.currency ?? currencyFallback)
            .trim()
            .toUpperCase();

        const page = Number.isFinite(Number(args?.page)) ? Number(args.page) : 1;
        const limit = Number.isFinite(Number(args?.limit)) ? Number(args.limit) : 10;
        const append = args?.append !== undefined ? Boolean(args.append) : true;
        const search = String(args?.search ?? args?.q ?? "").trim();

        const basePath = search ? "/product/search" : "/product/";
        const query = new URLSearchParams({
            country,
            currency,
            page: String(page),
            limit: String(limit),
        });
        if (search) query.set("q", search);

        const response = await Api.get(`${basePath}?${query.toString()}`);
        return {
            items: response.data?.data || [],
            pagination: response.data?.pagination || { page, limit, total: 0, hasMore: false },
            page,
            append,
            search,
        };
    }
);

export const getAllProducts = createAsyncThunk(
    "products/getAllProducts",
    async ({ country, currency, page = 1, limit = 10 }) => {
        const response = await Api.get(
            `/product/?country=${country}&currency=${currency}&page=${page}&limit=${limit}`
        );
        return response.data;
    }
);

export const fetchProductById = createAsyncThunk(
    "products/fetchProductById",
    async (args, thunkAPI) => {
        const { rejectWithValue, getState } = thunkAPI;
        const state = typeof getState === "function" ? getState() : null;
        const settings = state?.userSettings || {};

        const id = args?.id;
        const country = String(args?.country ?? settings?.shipToCountry ?? "US").trim().toUpperCase();
        const currencyFallback = "USD";
        const currency = String(args?.currency ?? settings?.currency ?? currencyFallback).trim().toUpperCase();
        const sku = String(args?.sku ?? args?.sku_id ?? "").trim();

        try {
            const query = new URLSearchParams({ country, currency });
            if (sku) query.set("sku", sku);
            const response = await Api.get(
                "/product/" + encodeURIComponent(String(id || "")) + "?" + query.toString(),
                { signal: thunkAPI.signal }
            );
            return response.data;
        } catch (err) {
            const status = err?.response?.status;
            const data = err?.response?.data;
            const message =
                (typeof data?.message === "string" && data.message) ||
                (typeof err?.message === "string" && err.message) ||
                "Request failed";

            // If the backend uses 404/400 for "not available in this country",
            // treat it as a handled state on the UI.
            if (status === 404 || status === 400) {
                return rejectWithValue({ status, message, data });
            }

            return rejectWithValue({ status: status || 0, message, data });
        }
    }
);

export const fetchAliProduct = createAsyncThunk(
    "products/fetchAliProduct",
    async (id) => {
        const response = await Api.get(`/product/aliexpress/${id}`);
        return response.data;
    }
);
export const fetchLogistic = createAsyncThunk(
    "products/fetchLogistic",
    async (data) => {
        const response = await Api.post("/logistic", data);
        return response.data;
    }
);

export const SaveAliProduct = createAsyncThunk(
    "products/saveAliProduct",
    async (id) => {
        const response = await Api.post(`/product/aliexpress/${id}`);
        return response.data;
    }
);

export const updateProduct = createAsyncThunk(
    "products/updateProduct",
    async ({ id, data }) => {
        const response = await Api.put(`/product/${id}`, data);
        return response.data;
    }
);


export const deleteProduct = createAsyncThunk(
    "products/deleteProduct",
    async (productId) => {
        const response = await Api.delete(`/product/${productId}`);
        return response.data;
    }
);

// New async thunks for edit functionality
export const getAllCountriesData = createAsyncThunk(
    "products/getAllCountriesData",
    async (productId) => {
        const response = await Api.get(`/product/edit/${productId}/all-countries`);
        return response.data;
    }
);

export const updateAllCountriesData = createAsyncThunk(
    "products/updateAllCountriesData",
    async ({ productId, ali_data }) => {
        const response = await Api.put(`/product/edit/${productId}/all-countries`, { ali_data });
        return response.data;
    }
);

export const refreshAllCountriesData = createAsyncThunk(
    "products/refreshAllCountriesData",
    async (productId) => {
        const response = await Api.post(`/product/edit/${productId}/refresh`);
        return response.data;
    }
);


const productSlice = createSlice({
    name: "products",
    initialState,
    reducers: {
        resetProducts: (state) => {
            state.products = [];
            state.status = "idle";
            state.error = null;
            state.lastSearchQuery = "";
            state.hasMore = true;
            state.currentPage = 1;
            state.pagination = {
                page: 1,
                limit: 10,
                total: 0,
                hasMore: true,
            };
        },
        clearSelectedProduct: (state) => {
            state.detailRequestId = null;
            state.status = "idle";
            state.error = null;
            state.selectedProduct = {};
        }
    },
    extraReducers: (builder) => {
        builder
            // Clear list on ship-to/currency changes to avoid stale cross-country items.
            .addCase(setShipToCountry, (state) => {
                state.products = [];
                state.status = "idle";
                state.error = null;
                state.lastSearchQuery = "";
                state.hasMore = true;
                state.currentPage = 1;
                state.pagination = {
                    page: 1,
                    limit: state.pagination?.limit ?? 10,
                    total: 0,
                    hasMore: true,
                };
            })
            .addCase(setSettingsSlice, (state, action) => {
                const nextShipTo = action?.payload?.shipToCountry;
                const nextCurrency = action?.payload?.currency;
                if (nextShipTo === undefined && nextCurrency === undefined) return;

                state.products = [];
                state.status = "idle";
                state.error = null;
                state.lastSearchQuery = "";
                state.hasMore = true;
                state.currentPage = 1;
                state.pagination = {
                    page: 1,
                    limit: state.pagination?.limit ?? 10,
                    total: 0,
                    hasMore: true,
                };
            })
            .addCase(fetchProducts.pending, (state) => {
                state.status = "loading";
            })
            .addCase(fetchProducts.fulfilled, (state, action) => {
                const { items, pagination, page, append, search } = action.payload;

                if (append && page > 1) {
                    const seen = new Set(state.products.map(productIdentity).filter(Boolean));
                    const uniqueIncoming = items.filter((item) => {
                        const key = productIdentity(item);
                        if (!key) return true;
                        if (seen.has(key)) return false;
                        seen.add(key);
                        return true;
                    });
                    state.products = [...state.products, ...uniqueIncoming];
                } else {
                    state.products = items;
                }

                state.status = "succeeded";
                state.lastSearchQuery = String(search || "").trim();
                state.pagination = pagination || state.pagination;
                state.currentPage = pagination?.page ?? page;
                state.hasMore = pagination?.hasMore ?? items.length > 0;
            })
            .addCase(fetchProducts.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })

            .addCase(getAllProducts.pending, (state) => {
                state.status = "loading";
            })
            .addCase(getAllProducts.fulfilled, (state, action) => {
                state.products = action.payload.data || [];
                if (action.payload.pagination) {
                    state.pagination = action.payload.pagination;
                    state.currentPage = action.payload.pagination.page;
                    state.hasMore = action.payload.pagination.hasMore;
                }
                state.status = "succeeded";
            })
            .addCase(getAllProducts.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })

            .addCase(deleteProduct.fulfilled, (state, action) => {
                state.products = state.products.filter(
                    (product) => product.id !== action.payload
                );
            })

            .addCase(fetchProductById.pending, (state, action) => {
                state.detailRequestId = action.meta.requestId;
                state.status = "loading";
                state.error = null;
            })
            .addCase(fetchProductById.fulfilled, (state, action) => {
                if (state.detailRequestId !== action.meta.requestId) return;
                state.status = "succeeded";
                state.selectedProduct = action.payload;
            })
            .addCase(fetchProductById.rejected, (state, action) => {
                if (state.detailRequestId !== action.meta.requestId) return;
                if (action.meta.aborted) { state.status = "idle"; return; }
                state.status = "failed";
                state.error = action.payload || action.error.message;
            })
            .addCase(fetchAliProduct.pending, (state) => {
                state.status = "loading";
            })
            .addCase(fetchAliProduct.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.error = "";
                state.selectedProduct = action.payload;
            })
            .addCase(fetchAliProduct.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.payload;
            })
            .addCase(SaveAliProduct.pending, (state) => {
                state.status = "loading";
            })
            .addCase(SaveAliProduct.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.error = "";
                state.selectedProduct = action.payload;
            })
            .addCase(SaveAliProduct.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(fetchLogistic.pending, (state) => {
                state.status = "loading";
            })
            .addCase(fetchLogistic.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.error = "";
                // Assuming the response contains logistic data
                state.selectedProduct.logistic = action.payload;
            })
            .addCase(fetchLogistic.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(updateProduct.pending, (state) => {
                state.status = "loading";
            })
            .addCase(updateProduct.fulfilled, (state, action) => {
                state.status = "succeeded";
                // Update the product in the products array
                const updated = action.payload.data;
                state.products = state.products.map((product) =>
                    product._id === updated._id ? updated : product
                );
                state.selectedProduct = updated;
            })
            .addCase(updateProduct.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(getAllCountriesData.pending, (state) => {
                state.status = "loading";
            })
            .addCase(getAllCountriesData.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.error = "";
                state.selectedProduct = action.payload.data;
            })
            .addCase(getAllCountriesData.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(updateAllCountriesData.pending, (state) => {
                state.status = "loading";
            })
            .addCase(updateAllCountriesData.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.error = "";
                state.selectedProduct = action.payload.data;
            })
            .addCase(updateAllCountriesData.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            })
            .addCase(refreshAllCountriesData.pending, (state) => {
                state.status = "loading";
            })
            .addCase(refreshAllCountriesData.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.error = "";
                state.selectedProduct = action.payload.data;
            })
            .addCase(refreshAllCountriesData.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.error.message;
            });
    },
});

export const selectProducts = (state) => state.products.products;
export const selectProductById = (state, productId) =>
    state.products.products.find((product) => product.id === productId);

// Selector to get two random products from the products list
export const selectTwoRandomProducts = (state) => {
    const products = state.products.products;
    if (products.length < 2) {
        return products; // Return all products if less than 2
    }

    // Create a copy of the products array to avoid mutating the original
    const shuffled = [...products].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 2);
};

// Selector to get three random products for slides (different from side banners)
export const selectThreeRandomProductsForSlides = (state) => {
    const products = state.products.products;
    if (products.length < 3) {
        return products; // Return all products if less than 3
    }

    // Create a copy of the products array to avoid mutating the original
    const shuffled = [...products].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 3);
};

export const { clearSelectedProduct, resetProducts } = productSlice.actions;
export default productSlice.reducer;
